import React, { useState, useEffect, useCallback, useRef } from 'react';
import api, { getApiErrorMessage } from '../../../services/api';
import { toast } from 'sonner';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Switch } from '../../../components/ui/switch';
import { Textarea } from '../../../components/ui/textarea';
import { Badge } from '../../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import { useExpenditureLabels, useDecimalFormat } from '../../../contexts/ExpenditureLabelsContext';
import TypeAheadLookup from '../../../components/TypeAheadLookup';
import { LoadingSpinner, EmptyState } from '../../../components/layout/SharedComponents';
import {
  ChevronDown, ChevronLeft, ChevronRight, X, Plus, Trash2,
  Layers, FileText, CheckSquare, Split, Paperclip,
  ScrollText, StickyNote, Package, Activity, Save,
  TrendingUp, BarChart2, Upload, Search, Wand2, Download,
  RefreshCw, Edit, FileSpreadsheet, ListOrdered, AlignJustify, Users
} from 'lucide-react';

export default function ItemSourcingModal({ open, onClose, onAdd }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);

  const search = async () => {
    if (!query.trim()) return;
    setLoading(true);
    try {
      const res = await api.post('/api/requisitions/item-sourcing', { query });
      setResults(res.data.results || []);
      if (res.data.error) toast.error('AI returned unexpected format, showing partial results');
    } catch (e) { toast.error(getApiErrorMessage(e, 'Sourcing failed')); }
    setLoading(false);
  };

  const CONF_COLORS = { high: 'text-green-600', medium: 'text-amber-600', low: 'text-red-600' };

  return (
    <Dialog open={open} onOpenChange={() => { onClose(); setResults([]); setQuery(''); }}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wand2 className="h-5 w-5 text-primary" /> AI Item Sourcing
          </DialogTitle>
        </DialogHeader>
        <div className="flex items-center gap-2 p-3 rounded-lg bg-primary/5 border border-primary/20 mb-4">
          <Wand2 className="h-4 w-4 text-primary flex-shrink-0" />
          <p className="text-sm">Describe what you want to source and AI will find suppliers and estimated prices for you.</p>
        </div>
        <div className="flex gap-2 mb-4">
          <Input value={query} onChange={e => setQuery(e.target.value)} placeholder='e.g. "ostrich eggs", "ergonomic office chairs", "industrial drill bits"'
            className="flex-1" onKeyDown={e => e.key === 'Enter' && search()} />
          <Button onClick={search} disabled={loading || !query.trim()}>
            {loading ? <><RefreshCw className="h-4 w-4 mr-1 animate-spin" />Searching...</> : <><Search className="h-4 w-4 mr-1" />Source</>}
          </Button>
        </div>
        {loading && (
          <div className="flex flex-col items-center py-8 gap-3">
            <RefreshCw className="h-8 w-8 text-primary animate-spin" />
            <p className="text-sm text-muted-foreground">AI is searching for sourcing options...</p>
          </div>
        )}
        {!loading && results.length > 0 && (
          <div className="space-y-3">
            <p className="text-sm font-medium">{results.length} sourcing options found for: <strong>{query}</strong></p>
            {results.map((r, i) => (
              <div key={i} className="border rounded-xl p-4 hover:shadow-sm transition-shadow">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="font-semibold text-sm">{r.supplier_name}</p>
                      <Badge variant="outline" className={`text-[10px] ${CONF_COLORS[r.confidence]}`}>{r.confidence} confidence</Badge>
                    </div>
                    <p className="text-sm text-muted-foreground mb-1">{r.description}</p>
                    <div className="flex flex-wrap gap-3 text-xs">
                      <span className="font-mono font-semibold text-primary text-sm">~${r.estimated_unit_price?.toFixed(2)} {r.currency} / {r.unit}</span>
                      {r.lead_time_days && <span className="text-muted-foreground">Lead time: {r.lead_time_days} days</span>}
                      {r.supplier_code_hint && <span className="text-muted-foreground font-mono">Hint: {r.supplier_code_hint}</span>}
                    </div>
                    {r.notes && <p className="text-xs text-muted-foreground mt-1 italic">{r.notes}</p>}
                  </div>
                  <Button size="sm" onClick={() => {
                    onAdd({ item_description: `${r.description} at ${r.supplier_name}`, quantity: 1, unit: r.unit || 'EACH', unit_price: r.estimated_unit_price || 0, pack_size: 1, discount: 0, supplier_code: '' });
                    toast.success('Item added from AI sourcing');
                    onClose(); setResults([]); setQuery('');
                  }}>Add to Req</Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
